<template>
  <a-layout-content class="my-4 mx-3">
    <div v-if="project" class="container">
      <div
        v-if="rolesWarningSummary && rolesWarningSummary.length > 0 && !loadingTable"
        style="cursor: pointer"
        @click="rolesWarningTableVisible = true"
      >
        <a-alert
          class="mb-3"
          message="Reports are not locked for all users"
          type="warning"
          show-icon
        />
      </div>
      <div class="flex-center-between mb-4">
        <div class="w-100 flex-center-between">
          <router-link
            :to="{ name: 'project', params: { projectId: project.id } }"
            tag="h3"
            class="m-0 action-default"
          >
            <a-icon type="arrow-left" class="mr-2" />Back
          </router-link>

          <h2 class="m-0 ml-2">
            New subproject invoice for
            <strong>{{ project.name }}</strong>
          </h2>
          <div class="flex-center">
            <portal-target name="create-invoice-filter-button"></portal-target>
            <a-tooltip :title="createDisabledTooltip">
              <span>
                <a-button
                  type="primary"
                  class="ml-2"
                  :disabled="isCreateDisabled"
                  @click="() => confirmStoreInvoice()"
                  >Create</a-button
                >
              </span>
            </a-tooltip>
          </div>
        </div>
      </div>

      <create-invoice-filter :filters="{ ...filtersRef }" @apply-filters="applyFilters" />
      <div class="card mt-4">
        <div class="d-flex justify-content-between mb-3">
          <h2 class="m-0">Summary</h2>
        </div>

        <a-row>
          <a-col :span="12" class="p-1">
            <div class="desc-item">
              <span class="label">Total logged time:</span>
              <span class="value">{{ totalAmount }} hour(s)</span>
            </div>
          </a-col>

          <a-col :span="12" class="p-1">
            <div class="desc-item">
              <span class="label">Total value:</span>
              <a-col class="ml-4">
                <div v-if="totalCostMap.size > 0">
                  <a-row
                    v-for="[currency, totalCost] in totalCostMap"
                    :key="currency"
                    class="text-right"
                  >
                    {{ totalCost / 100 + ",00 " + currency }}
                  </a-row>
                </div>
                <div v-else>-</div>
              </a-col>
            </div>
          </a-col>
        </a-row>

        <a-table
          v-if="rolesMap.size > 0"
          :columns="invoiceSummaryRolesColumns"
          :data-source="Array.from(rolesMap.values())"
          :pagination="false"
          :row-key="(roleSummary) => roleSummary.role.id"
          :scroll="{ x: 600 }"
        >
          <template #role="title, role">
            <span class="d-flex align-items-center">
              <span class="min-w-8 mr-2">
                <a-avatar :src="role.user.gravatar" size="small" />
              </span>
              <span>
                {{ role.role.title }} (
                <router-link :to="{ name: 'user', params: { id: role.user.id } }">
                  {{ role.user.name }} </router-link
                >)
              </span>
            </span>
          </template>

          <template #amount="hours">
            <span>
              {{ hours ? `${hours} hour(s)` : "-" }}
            </span>
          </template>

          <template #value="totalValue, role">
            <span>{{ totalValue / 100 + ",00 " + role.cost_currency }}</span>
          </template>
        </a-table>
      </div>

      <div class="card mt-4">
        <div class="d-flex justify-content-between mb-3">
          <h2 class="m-0">Reported invoice rows</h2>
          <a-button type="primary" @click="handleAddManualReportedRow">
            <a-icon type="plus" />Add manual row
          </a-button>
        </div>

        <a-table
          :columns="invoiceReportedItemsEditableTableColumns"
          :data-source="reportedInvoiceItems"
          :pagination="false"
          :scroll="{ x: 900 }"
          :row-key="(row) => row.local_key"
        >
          <template #description="description, row">
            <a-input v-model="row.description" @input="markReportedRowsEdited" />
            <small v-if="row.subproject_name" class="d-block mt-1">
              {{ row.subproject_name }}
            </small>
          </template>
          <template #amountMajor="amountMajor, row">
            <a-input
              :value="getReportedAmountInputValue(row)"
              inputmode="decimal"
              @input="(event) => onReportedAmountInput(row, event?.target?.value)"
              @blur="(event) => commitReportedAmount(row, event?.target?.value)"
              @pressEnter="(event) => commitReportedAmount(row, event?.target?.value)"
            />
          </template>
          <template #amountCurrency="amountCurrency, row">
            <span>{{ row.amount_currency }}</span>
          </template>
          <template #totalCost="totalCost, row">
            {{
              formatCentsWithCurrency(
                parseMajorAmountToCents(row.amount_major) || 0,
                row.amount_currency
              )
            }}
          </template>
          <template #actions="text, row">
            <a-icon
              type="minus-circle"
              class="action-danger"
              @click="handleRemoveReportedRow(row.local_key)"
            />
          </template>
        </a-table>

        <div class="mt-3">
          <div>
            <strong>Selected reports total:</strong>
            {{ formatCentsWithCurrency(selectedReportsTotalCents, selectedReportsCurrency) }}
          </div>
          <div>
            <strong>Invoice rows total:</strong>
            {{ formatCentsWithCurrency(reportedRowsTotalCentsValue, rowCurrency) }}
          </div>
          <div>
            <strong>Remaining amount:</strong>
            {{
              formatCentsWithCurrency(
                reportedRowsDiffCentsValue,
                selectedReportsCurrency || rowCurrency
              )
            }}
          </div>
        </div>
      </div>

      <!-- REPORTS TABLE -->
      <div class="card mt-4">
        <div class="d-flex justify-content-between mb-3">
          <h2 class="m-0">Reports</h2>
          <a-select
            v-model="selectedSubprojectFilter"
            mode="multiple"
            style="min-width: 320px"
            placeholder="Filter by subproject"
            allow-clear
            @change="onSubprojectFilterChange"
          >
            <a-select-option
              v-for="subprojectOption in subprojectFilterOptions"
              :key="subprojectOption.id"
              :value="subprojectOption.id"
            >
              {{ subprojectOption.name }}
            </a-select-option>
          </a-select>
        </div>

        <a-table
          v-if="loadingTable || filteredReports[0]"
          :columns="invoiceReportsColumns"
          :data-source="filteredReports"
          :loading="loadingTable"
          :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
          :pagination="false"
          :scroll="{ x: 600 }"
          :row-key="(invoice) => invoice.id"
        >
          <template #type="type, report">
            <span class="d-flex align-items-center">
              <a-tag v-if="type === ReportType.TIME" :color="Colors.blue_darker">
                {{ type.replace("-", " ") }}
              </a-tag>
              <a-icon
                v-if="type === ReportType.TIME && !report.locked"
                class="text-red mr-2"
                type="unlock"
              />
              <a-tag v-if="type === ReportType.EXPENSE" :color="Colors.blue">
                {{ type.replace("-", " ") }}
              </a-tag>

              <a-icon v-if="report.cost_center && report.cost_center.billable" type="dollar" />
            </span>
          </template>

          <template #role="role, report">
            <span v-if="report.type === ReportType.TIME" class="d-flex align-items-center">
              <span class="min-w-8 mr-2">
                <a-avatar :src="report.user.gravatar" size="small" />
              </span>
              <span class="flex flex-col">
                <div>
                  {{ role }}
                </div>
                <router-link :to="{ name: 'user', params: { id: report.user.id } }">
                  {{ report.user.name }}
                </router-link>
              </span>
            </span>

            <span v-if="report.type === ReportType.EXPENSE" class="d-flex align-items-center">
              <a-avatar :src="report.user.gravatar" size="small" class="mr-2" />
              <router-link :to="{ name: 'user', params: { id: report.user.id } }">
                {{ report.user.name }}
              </router-link>
            </span>
          </template>

          <template #amount="amount">
            <span>{{ amount ? `${amount} hour(s)` : "-" }}</span>
          </template>

          <template #hourly="text">
            <span>{{ text ? toMoney(text) : "-" }}</span>
          </template>

          <template #comment="text, report">
            <div v-if="report.comment">
              <a-popover v-if="report.comment.length > COMMENT_MAX_LENGTH">
                <template #content>
                  <p style="max-width: 300px; max-height: 200px; overflow-y: scroll">
                    {{ report.comment }}
                  </p>
                </template>
                <div>
                  {{ report.comment.substring(0, COMMENT_MAX_LENGTH) + "…" }}
                </div>
              </a-popover>
              <div v-else>{{ report.comment }}</div>
            </div>
          </template>
        </a-table>
      </div>
      <!-- Roles Warning TABLE -->
      <RolesWarningTable
        :visible="rolesWarningTableVisible"
        :roles-warning-summary="rolesWarningSummary"
        @close="rolesWarningTableVisible = false"
      />
    </div>
  </a-layout-content>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance, computed, watch, onMounted } from "vue";
import { invoiceReportsColumns } from "@/modules/reports/_utils/tables";
import { invoiceSummaryRolesColumns } from "@/modules/roles/_utils/tables";
import { apiGetProject } from "@/modules/projects/_utils/api";
import { ProjectInterface } from "@/modules/projects/types";
import { apiStoreProjectInvoice } from "../_utils/api";
import Colors from "@/assets/scss/_colors.module.scss";
import { ReportType } from "@/modules/reports/types";
import useMixin from "@/useMixin";
import { useInvoicesStore } from "@/modules/invoices/_store";
import createInvoiceFilter from "../_components/CreateInvoiceFilter.vue";
import {
  CreateInvoiceInterface,
  RolesWarningInterface,
  ReportedInvoiceItemFormInterface,
} from "../types";
import RolesWarningTable from "../_components/RolesWarningTable.vue";
import {
  formatCentsWithCurrency,
  formatCentsToMajorAmount,
  normalizeReportedItemForForm,
  normalizeReportedItemsForPayload,
  parseMajorAmountToCents,
  reportedRowsDiffCents,
  reportedRowsTotalCents,
  validateReportedRows,
} from "@/modules/invoices/_utils/reported-invoice-items";
import { invoiceReportedItemsEditableTableColumns } from "@/modules/invoices/_utils/tables";
import { useReportedInvoiceRows } from "@/modules/invoices/_composables/useReportedInvoiceRows";

// Pinia
const invoicesStore = useInvoicesStore();
const reports = computed(() => invoicesStore.reports);
const rolesWarningSummary = computed(
  () => invoicesStore.rolesWarningSummary as Array<RolesWarningInterface>
);

// Mixins
const { toMoney } = useMixin();

// Instances
const instance = getCurrentInstance();
const $route = instance?.proxy.$route;
const $router = instance?.proxy.$router;
const $error = instance?.proxy.$error;
const $confirm = instance?.proxy.$confirm;
const $message = instance?.proxy.$message;

// Data properties
const selectedRowKeys = ref<Array<any>>([]);
const totalCostMap = ref<Map<string, number>>(new Map());
const totalAmount = ref<number>(0);
const rolesMap = ref<Map<number, any>>(new Map());
const project = ref<ProjectInterface | null>(null);
const COMMENT_MAX_LENGTH = ref<number>(50);
const filtersRef = ref<CreateInvoiceInterface>({});
const loadingTable = ref<boolean>(false);
const rolesWarningTableVisible = ref<boolean>(false);
const selectedSubprojectFilter = ref<Array<number>>([]);
const reportedInvoiceItems = ref<Array<ReportedInvoiceItemFormInterface>>([]);
const reportedRowsEdited = ref<boolean>(false);

// Watchers
watch(reports, () => {
  selectedSubprojectFilter.value = [];
  onSelectChange([]);
  preselect();
});

// Lifecycle hooks
onMounted(() => {
  apiGetProject(parseInt($route?.params.projectId as string)).then(
    (res: any) => (project.value = res.data.data)
  );
});

// Component methods
const onSelectChange = (s: Array<any>): void => {
  const previousSelectionCurrency = getSelectedReportsCurrencyAndTotal(
    selectedRowKeys.value
  ).currency;
  const { selectedKeys, rejectedCount } = normalizeSelectionToSingleCurrency(
    s,
    selectedRowKeys.value
  );
  if (rejectedCount > 0) {
    $message?.error(
      "You can only invoice one currency at a time. Reports with a different currency were not added.",
      4
    );
  }
  selectedRowKeys.value = selectedKeys;
  computeInvoiceSummary();
  const nextSelectionCurrency = getSelectedReportsCurrencyAndTotal(selectedRowKeys.value).currency;

  if (!selectedRowKeys.value.length) {
    reportedInvoiceItems.value = [];
    resetReportedAmountDrafts();
    reportedRowsEdited.value = false;
    return;
  }

  if (
    previousSelectionCurrency &&
    nextSelectionCurrency &&
    previousSelectionCurrency !== nextSelectionCurrency
  ) {
    regenerateReportedInvoiceItemsFromDefaults();
    reportedRowsEdited.value = false;
    return;
  }

  if (!reportedRowsEdited.value) {
    regenerateReportedInvoiceItemsFromDefaults();
  }
};

const reportMatchesSubprojectFilter = (report: any): boolean => {
  if (!selectedSubprojectFilter.value.length) {
    return true;
  }
  if (report.type !== ReportType.TIME) {
    return false;
  }
  const subprojectIds = (report.subproject_reports || []).map((subprojectReport: any) =>
    Number(subprojectReport.subproject_id)
  );
  return subprojectIds.some((subprojectId: number) =>
    selectedSubprojectFilter.value.includes(subprojectId)
  );
};

const onSubprojectFilterChange = (): void => {
  const visibleIds = new Set(filteredReports.value.map((report: any) => report.id));
  selectedRowKeys.value = selectedRowKeys.value.filter((selectedId: number) =>
    visibleIds.has(selectedId)
  );
  onSelectChange([...selectedRowKeys.value]);
};

const preselect = (): void => {
  const preselectedRowKeys: Array<number> = [];
  reports.value.forEach((r: any) => {
    if ((r.locked && r.cost_center && r.cost_center.billable) || r.type === ReportType.EXPENSE) {
      preselectedRowKeys.push(r.id);
    }
  });
  onSelectChange(preselectedRowKeys);
};

const applyFilters = (filters: CreateInvoiceInterface): void => {
  filtersRef.value = filters;
  handleGetReportsPage({
    filters: filtersRef.value,
  });
};

const handleGetReportsPage = ({
  filters = filtersRef.value,
}: {
  filters?: CreateInvoiceInterface;
}): void => {
  loadingTable.value = true;

  invoicesStore
    .getReportsForInvoice(parseInt($route?.params.projectId as string), {
      ...filters,
    })
    .then(() => {
      loadingTable.value = false;
    });
};
const computeInvoiceSummary = (): void => {
  totalCostMap.value = new Map();
  totalAmount.value = 0;
  rolesMap.value = new Map();

  selectedRowKeys.value.forEach((v) => {
    // Get report for selected row.
    let report = reports.value.find((r: any) => r.id === v);

    // If is expense report simply add cost and return.
    if (report.type === ReportType.EXPENSE) {
      incrementTotalCostMapValueBy(report.cost.currency_code, report.cost.value);
      return;
    }

    // If is time report continue and start by computing cost.
    totalAmount.value += report.amount;
    incrementTotalCostMapValueBy(report.total_cost.currency_code, report.total_cost.value);

    // Add role to map if not there yet.
    if (!rolesMap.value.has(report.role.id)) {
      rolesMap.value.set(report.role.id, {
        role: { ...report.role },
        user: { ...report.user },
        amount: 0,
        cost: 0,
        cost_currency: "",
      });
    }

    // Update role data in map.
    let role = rolesMap.value.get(report.role.id);
    role.amount += parseInt(report.amount);
    role.cost += report.total_cost.value;
    role.cost_currency = report.total_cost.currency_code;
  });
};

const incrementTotalCostMapValueBy = (key: string, increment: number): void => {
  if (!totalCostMap.value.has(key)) {
    totalCostMap.value.set(key, 0);
  }
  totalCostMap.value.set(key, totalCostMap.value.get(key)! + increment);
};

const reportCurrencyCode = (report: any): string | null => {
  const reportCost = report.type === ReportType.EXPENSE ? report.cost : report.total_cost;
  return reportCost?.currency_code || null;
};

const normalizeSelectionToSingleCurrency = (
  selectedKeys: Array<number>,
  previousSelectedKeys: Array<number>
): { selectedKeys: Array<number>; rejectedCount: number } => {
  const previousSelectionSet = new Set(previousSelectedKeys);
  const hasRetainedPreviousSelection = selectedKeys.some((id) => previousSelectionSet.has(id));
  let selectedCurrency: string | null = null;
  if (hasRetainedPreviousSelection) {
    const retainedPreviousId = selectedKeys.find((id) => previousSelectionSet.has(id));
    const retainedPreviousReport = reports.value.find(
      (candidate: any) => candidate.id === retainedPreviousId
    );
    selectedCurrency = retainedPreviousReport ? reportCurrencyCode(retainedPreviousReport) : null;
  }

  let rejectedCount = 0;
  const normalizedSelection = selectedKeys.filter((id) => {
    const report = reports.value.find((candidate: any) => candidate.id === id);
    const currency = report ? reportCurrencyCode(report) : null;
    if (!currency) {
      return false;
    }
    if (!selectedCurrency) {
      selectedCurrency = currency;
      return true;
    }
    const isMatchingCurrency = currency === selectedCurrency;
    if (!isMatchingCurrency) {
      rejectedCount += 1;
    }
    return isMatchingCurrency;
  });

  return {
    selectedKeys: normalizedSelection,
    rejectedCount,
  };
};

const buildDefaultReportedInvoiceItems = (): Array<ReportedInvoiceItemFormInterface> => {
  const selectedReports = reports.value.filter((report: any) =>
    selectedRowKeys.value.includes(report.id)
  );
  const subprojectTotals = new Map<string, any>();
  const uncategorizedTotals = new Map<string, number>();

  selectedReports.forEach((report: any) => {
    const currency =
      report.type === ReportType.EXPENSE
        ? report.cost?.currency_code
        : report.total_cost?.currency_code;
    if (!currency) {
      return;
    }

    if (!uncategorizedTotals.has(currency)) {
      uncategorizedTotals.set(currency, 0);
    }

    if (report.type !== ReportType.TIME) {
      uncategorizedTotals.set(
        currency,
        uncategorizedTotals.get(currency)! + (Number(report.cost?.value) || 0)
      );
      return;
    }

    const hourlyRateCents = Number(report.hourly_rate?.value) || 0;
    const subprojectReports = report.subproject_reports || [];
    let categorizedCents = 0;

    subprojectReports.forEach((subprojectReport: any) => {
      const rowCents = Math.round((Number(subprojectReport.amount) || 0) * hourlyRateCents);
      categorizedCents += rowCents;
      const key = `${subprojectReport.subproject_id}:${currency}`;
      if (!subprojectTotals.has(key)) {
        subprojectTotals.set(key, {
          kind: "subproject",
          source_subproject_id: subprojectReport.subproject_id,
          subproject_name: subprojectReport.fortnox_alias ? subprojectReport.name : undefined,
          description: subprojectReport.fortnox_alias || subprojectReport.name || "Subproject",
          amount_cents: 0,
          amount_currency: currency,
          amount_major: "0.00",
        });
      }
      subprojectTotals.get(key).amount_cents += rowCents;
      subprojectTotals.get(key).amount_major = formatCentsToMajorAmount(
        subprojectTotals.get(key).amount_cents
      );
    });

    uncategorizedTotals.set(
      currency,
      uncategorizedTotals.get(currency)! +
        ((Number(report.total_cost?.value) || 0) - categorizedCents)
    );
  });

  const rows: Array<ReportedInvoiceItemFormInterface> = Array.from(subprojectTotals.values()).map(
    (item: any) => ({
      ...normalizeReportedItemForForm(item),
      subproject_name: item.subproject_name,
    })
  );
  uncategorizedTotals.forEach((total, currency) => {
    if (total > 0) {
      rows.push(
        normalizeReportedItemForForm({
          kind: "uncategorized",
          description: "Uncategorized",
          amount_cents: total,
          amount_currency: currency,
        })
      );
    }
  });

  return rows;
};

const regenerateReportedInvoiceItemsFromDefaults = (): void => {
  const localDefaults = buildDefaultReportedInvoiceItems();
  reportedInvoiceItems.value = localDefaults.map((item: any) => withReportedRowKey(item));
};

const selectedReportsTotals = computed(() => getSelectedReportsCurrencyAndTotal());
const selectedReportsCurrency = computed(() => selectedReportsTotals.value.currency);
const selectedReportsTotalCents = computed(() => selectedReportsTotals.value.total);
const reportedRowsTotalCentsValue = computed(() =>
  reportedRowsTotalCents(reportedInvoiceItems.value)
);
const reportedRowsDiffCentsValue = computed(() =>
  reportedRowsDiffCents(selectedReportsTotalCents.value, reportedRowsTotalCentsValue.value)
);
const rowCurrency = computed(() => reportedInvoiceItems.value[0]?.amount_currency || null);
const hasReportedRowsMismatch = computed(() => reportedRowsDiffCentsValue.value !== 0);
const createDisabledTooltip = computed(() =>
  hasReportedRowsMismatch.value
    ? "Create is disabled because remaining amount must be 0.00 before invoicing."
    : ""
);
const isCreateDisabled = computed(() => {
  const validation = validateReportedRows({
    rows: reportedInvoiceItems.value,
    selectedReportsCurrency: selectedReportsCurrency.value,
    selectedReportsTotalCents: selectedReportsTotalCents.value,
  });
  return (
    selectedRowKeys.value.length < 1 || !selectedReportsTotals.value.currency || !validation.isValid
  );
});

const markReportedRowsEdited = (): void => {
  reportedRowsEdited.value = true;
};

const {
  withReportedRowKey,
  resetReportedAmountDrafts,
  handleAddManualReportedRow,
  onReportedAmountInput,
  getReportedAmountInputValue,
  commitReportedAmount,
  handleRemoveReportedRow,
} = useReportedInvoiceRows({
  rows: reportedInvoiceItems,
  rowKeyPrefix: "reported-row",
  defaultCurrency: computed(
    () => reportedInvoiceItems.value[0]?.amount_currency || selectedReportsCurrency.value || "SEK"
  ),
  onRowsEdited: markReportedRowsEdited,
});

const getSelectedReportsCurrencyAndTotal = (
  keys: Array<number> = selectedRowKeys.value
): { currency: string | null; total: number } => {
  let currency: string | null = null;
  let total = 0;
  const selectedReports = reports.value.filter((report: any) => keys.includes(report.id));
  selectedReports.forEach((report: any) => {
    const reportCost = report.type === ReportType.EXPENSE ? report.cost : report.total_cost;
    total += Number(reportCost?.value) || 0;
    const reportCurrency = reportCost?.currency_code;
    if (!currency) {
      currency = reportCurrency;
    }
  });
  return { currency, total };
};

const subprojectFilterOptions = computed(() => {
  const subprojects = new Map<number, string>();
  reports.value.forEach((report: any) => {
    if (report.type !== ReportType.TIME) {
      return;
    }
    (report.subproject_reports || []).forEach((subprojectReport: any) => {
      const subprojectId = Number(subprojectReport.subproject_id);
      if (!subprojects.has(subprojectId)) {
        subprojects.set(subprojectId, subprojectReport.name || `Subproject ${subprojectId}`);
      }
    });
  });

  return Array.from(subprojects.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredReports = computed(() =>
  reports.value.filter((report: any) => reportMatchesSubprojectFilter(report))
);

const checkReportedInvoiceRows = (): boolean => {
  const validation = validateReportedRows({
    rows: reportedInvoiceItems.value,
    selectedReportsCurrency: selectedReportsCurrency.value,
    selectedReportsTotalCents: selectedReportsTotalCents.value,
  });

  if (!validation.hasRows) {
    $error?.({
      title: "Missing invoice rows",
      content: "Please add at least one reported invoice row.",
    });
    return false;
  }

  if (validation.hasInvalidAmount) {
    $error?.({
      title: "Invalid amount",
      content: "Please provide valid row amounts (up to 2 decimals).",
    });
    return false;
  }

  if (validation.hasCurrencyMismatch) {
    $error?.({
      title: "Currency mismatch",
      content: "Reported invoice row currency must match selected reports currency.",
    });
    return false;
  }

  if (validation.hasTotalMismatch) {
    $error?.({
      title: "Invalid row total",
      content: "Reported invoice rows total must match selected reports total.",
    });
    return false;
  }

  return true;
};

const serializedReportedInvoiceItems = (): Array<any> => {
  return normalizeReportedItemsForPayload(reportedInvoiceItems.value);
};

const confirmStoreInvoice = (): void => {
  if (selectedRowKeys.value.length < 1) {
    storeEmptyInvoiceError();
    return;
  }
  if (!checkReportedInvoiceRows()) {
    return;
  }

  const mod = $confirm?.({
    title: `Are you sure you want to create a new invoice to ${project.value?.client.name} for the project ${project.value?.name}?`,
    okText: "Yes",
    cancelText: "No",
    onOk() {
      if (project.value) {
        apiStoreProjectInvoice(project.value.id, {
          report_ids: selectedRowKeys.value,
          status: "generated",
          type: "ReportedInvoice",
          reported_invoice_items: serializedReportedInvoiceItems(),
        })
          .then((res: any) => {
            $message?.success("Invoice created successfully!", 3);
            mod?.destroy();
            if (project.value) {
              $router?.push({
                name: "invoice",
                params: {
                  projectId: project.value?.id + "",
                  invoiceId: res.data.data.id,
                },
              });
            }
          })
          .catch(() => $message?.error("Couldn't create invoice!", 3));
      }
    },
  });
};

const storeEmptyInvoiceError = () => {
  $error?.({
    title: "The invoice you are trying to create does not contain any reports!",
  });
};
</script>
